import { AnalyticsStore, PageStore } from 'bernie-plugin-mobx';
import { inject } from 'mobx-react';
import { useHistory, useLocation } from 'react-router-dom';
import * as React from 'react';

import { Context } from 'bernie-context';
import { updateSearch } from 'bernie-core';

import { CustomerNotification, CustomerNotificationLocation } from '@shared-ui/customer-notifications';

import { SearchResultsStore } from 'stores/search-results-store';

import { classNames } from 'uitk-react-utils';
import { UitkSpacing } from 'uitk-react-spacing';

import { History, Location } from 'history';
import { SimpleLazyLoadWrapper } from 'components/common/lazy/simple-lazy-load-wrapper';

interface CustomerNotificationsProps {
  location: CustomerNotificationLocation;
  page?: PageStore;
  classes?: string;
  regionId?: string;
  startDate?: string;
  endDate?: string;
  context?: Context;
  analytics?: AnalyticsStore;
  searchResults?: SearchResultsStore;
}

const handleClick = (history: History, location: Location) => (isChecked: boolean) => {
  updateSearch({ history, location, newParams: { useRewards: String(isChecked) }, replace: true });
};

export const CustomerNotifications: React.FC<CustomerNotificationsProps> = inject(
  'page',
  'context',
  'searchResults'
)(
  React.memo((props: CustomerNotificationsProps) => {
    const { location: notificationLocation, page, classes, regionId, startDate, endDate, context, searchResults } = props;
    const location = useLocation();
    const history = useHistory();
    const historyLocation = history.location;
    const searchCriteria = searchResults?.searchCriteria;
    const useRewards = Boolean(searchCriteria && searchCriteria.useRewards === 'true');

    if (!page || !page.pageId) {
      return null;
    }

    let referer;
    if (context?.site?.domain) {
      const { domain } = context.site;
      const search = location.search || '';
      const path = location.pathname || '';
      referer = `https://${domain}${path}${search}`;
    }

    const customerNotificationsClasses = classes && classNames({ [classes]: classes });
    let customerNotificationInputs;

    if (Number(regionId)) {
      customerNotificationInputs = {
        notificationLocation,
        xPageId: page.pageId,
        optionalContext: {
          journeyCriterias: [
            {
              destination: {
                regionId: regionId,
              },
              dateRange: {
                start: startDate,
                end: endDate,
              },
            },
          ],
        },
      };
    } else {
      customerNotificationInputs = {
        notificationLocation,
        xPageId: page.pageId,
      };
    }

    if (notificationLocation === 'IN_PAGE') {
      return (
        <UitkSpacing padding={{ blockstart: 'two' }}>
          <div className={customerNotificationsClasses} data-stid={'one-key-customer-notification'}>
            <CustomerNotification
              inputs={{
                xPageId: page.pageId,
                notificationLocation,
                optionalContext: {
                  referer,
                },
              }}
              skipSsr
              switchHandler={handleClick(history, historyLocation)}
              useRewards={useRewards}
            />
          </div>
        </UitkSpacing>
      );
    }

    if (notificationLocation === 'FOOTER') {
      return (
        <UitkSpacing padding={{ blockstart: 'two' }}>
          <div className={customerNotificationsClasses} data-stid={'footer-customer-notification'}>
            <SimpleLazyLoadWrapper>
              <CustomerNotification
                inputs={{
                  xPageId: page.pageId,
                  notificationLocation,
                  optionalContext: {
                    referer,
                  },
                }}
              />
            </SimpleLazyLoadWrapper>
          </div>
        </UitkSpacing>
      );
    }

    return (
      <UitkSpacing padding={{ blockstart: 'two' }}>
        <div className={customerNotificationsClasses}>
          <CustomerNotification
            inputs={customerNotificationInputs as { notificationLocation: typeof notificationLocation; xPageId: string }}
          />
        </div>
      </UitkSpacing>
    );
  })
);
